// ── Export-Daten-Builder ────────────────────────────────────────────────────
// Reine ES-Module-Funktionen für Year- + Asset-Export-Datenaufbau.
// WICHTIG: Keine Browser-Deps (kein Firebase, kein localStorage, kein window).
// Wird sowohl vom Client (exportService.js) als auch vom Server
// (/api/_lib/) importiert — deshalb muss diese Datei pure bleiben.

import { calcAssetStats, calcAcquisitionAvgPrice, calcAssetInvestment, calcAssetCashIn, calcAssetCashOut } from './portfolioCalc';
import { buildTaxLots } from './taxLots';
import { dropScamDustTxs } from './txView';
import { fifo, getTaxableEvents, buildCausalSort } from './fifo';
import { buildAustrianPool, austrianRemainingByCoin } from './austrianCost';
import { buildReport } from './reportEngine';
import { rulesFor } from './taxRules';
import { COIN_MAP } from './coingecko';
import { SWAP_EUR_SET, deriveSwapEurValue, swapOutSym, swapInSym } from './reportModel';

// ── Helpers ─────────────────────────────────────────────────────────────────

// Verdichtet steuerpflichtige Events nach (datum, typ, coinOut, coinIn, stpfl,
// costBasisUnknown). Binance splittet User-Trades in viele Sekunden-Events;
// ohne Verdichtung blaeht das die PDF auf 50+ Seiten. Aggregiert menge, mengeOut,
// mengeIn, erloes/preis, ak, gv, fee; konkateniert soldLots; bildet hf-Range.
// FIFO-Breakdown bleibt vollstaendig (jeder Erwerb einzeln gelistet) -- §23-konform.
export function mergeTaxableEventsByDay(events) {
  if (!Array.isArray(events) || events.length === 0) return [];
  const groups = new Map();
  for (const ev of events) {
    const key = [
      ev.datum,
      ev.typ,
      ev.typ === 'swap' ? (ev.coinOut || '') : (ev.coin || ''),
      ev.typ === 'swap' ? (ev.coinIn  || '') : '',
      ev.stpfl ? '1' : '0',
      ev.costBasisUnknown ? 'u' : 'k',
    ].join('|');
    const g = groups.get(key);
    // effectiveErloes (fifo.js): Stablecoin/Fiat-Quote-aufgeloester EUR-Wert.
    // erloes: explizit gesetzter Wert. preis: Roh-Eingabewert (oft 0 für Swaps).
    // mengeIn-Fallback: bei Fiat-Quote-Swap (XLM→EUR) ist mengeIn direkt EUR.
    const coinInUp = String(ev.coinIn || '').toUpperCase();
    const isFiatQuote = ev.typ === 'swap' && PDF_FIAT_SET.has(coinInUp);
    const erloesRaw = Number(
      ev.effectiveErloes ?? ev.erloes ?? ev.preis ??
      (isFiatQuote ? ev.mengeIn : 0) ?? 0
    );
    if (!g) {
      groups.set(key, {
        ...ev,
        menge:    Number(ev.menge    || 0),
        mengeOut: Number(ev.mengeOut || 0),
        mengeIn:  Number(ev.mengeIn  || 0),
        erloes:   erloesRaw,
        preis:    erloesRaw,
        effectiveErloes: erloesRaw,
        ak:       Number(ev.ak || 0),
        gv:       ev.gv == null ? null : Number(ev.gv),
        gebuehr:  Number(ev.gebuehr || 0),
        hfMin:    Number.isFinite(ev.hf) ? ev.hf : null,
        hfMax:    Number.isFinite(ev.hf) ? ev.hf : null,
        kaufDatumMin: ev.kaufDatum || null,
        kaufDatumMax: ev.kaufDatum || null,
        soldLots: [...(ev.soldLots || [])],
        _count: 1,
      });
    } else {
      g.menge    += Number(ev.menge    || 0);
      g.mengeOut += Number(ev.mengeOut || 0);
      g.mengeIn  += Number(ev.mengeIn  || 0);
      g.erloes   += erloesRaw;
      g.preis     = g.erloes;
      g.effectiveErloes = g.erloes;
      g.ak       += Number(ev.ak || 0);
      g.gebuehr  += Number(ev.gebuehr || 0);
      if (g.gv != null && ev.gv != null) g.gv += Number(ev.gv);
      else if (g.gv == null) g.gv = ev.gv == null ? null : Number(ev.gv);
      if (Number.isFinite(ev.hf)) {
        g.hfMin = g.hfMin == null ? ev.hf : Math.min(g.hfMin, ev.hf);
        g.hfMax = g.hfMax == null ? ev.hf : Math.max(g.hfMax, ev.hf);
      }
      if (ev.kaufDatum) {
        if (!g.kaufDatumMin || ev.kaufDatum < g.kaufDatumMin) g.kaufDatumMin = ev.kaufDatum;
        if (!g.kaufDatumMax || ev.kaufDatum > g.kaufDatumMax) g.kaufDatumMax = ev.kaufDatum;
      }
      g.soldLots.push(...(ev.soldLots || []));
      g._count += 1;
    }
  }
  const out = [];
  for (const g of groups.values()) {
    // hf als Number (gleich) oder als "min-max" Range (variabel)
    g.hf = g.hfMin === g.hfMax ? g.hfMin : `${g.hfMin}-${g.hfMax}`;
    g.kaufDatum = g.kaufDatumMin === g.kaufDatumMax
      ? g.kaufDatumMin
      : `${g.kaufDatumMin} – ${g.kaufDatumMax}`;
    delete g.hfMin; delete g.hfMax;
    delete g.kaufDatumMin; delete g.kaufDatumMax;
    out.push(g);
  }
  // Chronologisch aufsteigend (für PDF-Lesefluss)
  out.sort((a, b) => (a.datum < b.datum ? -1 : a.datum > b.datum ? 1 : 0));
  return out;
}

// ── Veräußerungsübersicht: per-Lot-Zeilen (Single Source) ──────────────────
// Wandelt die verdichteten taxableEvents in eine FLACHE Liste — eine Zeile pro
// verbrauchtem Anschaffungs-Lot. Genau diese Aufstellung fragt das Finanzamt an:
// pro Veräußerung müssen Verkaufsdatum, Menge, Coin, Verkaufspreis, zugehörige
// Anschaffungskosten und Anschaffungsdatum ersichtlich sein. Jedes matched Lot
// ist eine eigenständige Veräußerung → eigene Zeile mit eigener Haltedauer +
// 1-Jahres-Frist-Beurteilung (genauer als die sale-level-Sammelangabe).
//
// Wird von Excel (excelBuilder) UND PDF (pdfBuilder) genutzt — identische Optik.
//
// Reconciliation (exakt, modulo 2-Dezimal-Rundung der Engine):
//   gv = Verkaufspreis(brutto) − Gebuehr(anteilig) − Anschaffungskosten
//   Summe(Zeilen-gv pro Event) == ev.gv   (Engine: gv = netProceeds − ak)
//   Summe(Zeilen-Anschaffung)  == ev.ak
//   Summe(Zeilen-Menge)        == ev.menge / ev.mengeOut
// costBasisUnknown / lot-los: Verkauf wird TROTZDEM gelistet (Datum/Menge/Coin/
// Verkaufspreis nachweispflichtig), Anschaffung = null + Hinweis „nachzutragen".
export function buildDisposalLotRows(taxableEvents, opts = {}) {
  if (!Array.isArray(taxableEvents)) return [];
  // average = AT (gleitender Durchschnitt): Status nach Neu-/Altvermögen statt
  // nach 1-Jahres-Frist. DE (kein opts) bleibt unverändert.
  const average = opts.costBasisMethod === 'average';
  const rows = [];
  for (const ev of taxableEvents) {
    const coin         = ev.coin || ev.coinOut || '';
    const verkaufDatum = ev.datum || '';
    const erloesTotal  = Number(ev.effectiveErloes ?? ev.erloes ?? ev.preis ?? 0);
    const gebuehrTotal = Number(ev.gebuehr || 0);
    const mengeTotal   = Number(ev.menge || ev.mengeOut || 0);
    const lots = Array.isArray(ev.soldLots)
      ? ev.soldLots.filter(l => l && Number(l.use) > 0)
      : [];
    const totalUse = lots.reduce((s, l) => s + Number(l.use), 0);

    // Verkauf ohne nachvollziehbare Anschaffung — trotzdem listen (nachweispflichtig).
    if (ev.costBasisUnknown || lots.length === 0 || totalUse <= 0) {
      rows.push({
        verkaufDatum,
        coin,
        lotMenge:           mengeTotal,
        verkaufspreis:      erloesTotal > 0 ? erloesTotal : null,
        gebuehr:            gebuehrTotal > 0 ? gebuehrTotal : null,
        anschaffungsDatum:  null,
        anschaffungsKosten: null,
        haltedauerTage:     null,
        status:             'Anschaffung offen',
        gv:                 null,
        unbekannt:          true,
        fallback:           !!ev.fallbackUsed,
        istErsteZeile:      true,
        lotAnzahl:          1,
      });
      continue;
    }

    lots.forEach((l, idx) => {
      const use     = Number(l.use);
      const stueck  = Number(l.stueck) || 0;
      const anteil  = use / totalUse;
      const ak      = use * stueck;                 // Anschaffungskosten dieses Lots
      const erloes  = erloesTotal * anteil;         // Veräußerungspreis (brutto, anteilig)
      const gebuehr = gebuehrTotal * anteil;        // anteilige Veräußerungskosten
      // Haltedauer pro Lot — exakt je Erwerb (floor, integer-Tage).
      const haltedauerTage = (l.datum && verkaufDatum)
        ? Math.floor((new Date(verkaufDatum) - new Date(l.datum)) / 86400000)
        : null;
      const unter1J = haltedauerTage == null ? null : haltedauerTage < 365;
      let status;
      if (average) {
        // AT: Neuvermögen (immer Realisierung) vs. Altvermögen (1-Jahres-Frist).
        if (l.alt) status = unter1J ? 'Altvermögen · unter 1 Jahr' : 'Altvermögen · über 1 Jahr';
        else status = 'Neuvermögen';
      } else {
        status = unter1J == null ? '—' : (unter1J ? 'Unter 1 Jahr' : 'Über 1 Jahr');
      }
      rows.push({
        verkaufDatum,
        coin,
        lotMenge:           use,
        verkaufspreis:      erloes,
        gebuehr:            gebuehr > 0 ? gebuehr : null,
        anschaffungsDatum:  l.datum || null,
        anschaffungsKosten: ak,
        haltedauerTage,
        status,
        gv:                 erloes - gebuehr - ak,
        unbekannt:          false,
        fallback:           !!ev.fallbackUsed,
        istErsteZeile:      idx === 0,
        lotAnzahl:          lots.length,
      });
    });
  }
  return rows;
}

export function dateKey(tx) {
  return tx.effectiveTimestamp?.slice(0, 10) || tx.datum || '';
}

export function txInvolvesSym(tx, sym) {
  const s = String(sym).toUpperCase();
  if (tx.typ === 'swap') {
    return String(tx.coinOut).toUpperCase() === s || String(tx.coinIn).toUpperCase() === s;
  }
  return String(tx.coin).toUpperCase() === s;
}

export function txInYear(tx, year) {
  const d = dateKey(tx);
  return d.startsWith(String(year));
}

// Kausale Sortierung — identisch zur FIFO-Engine. Sorgt dafür, dass am
// gleichen Tag die Reihenfolge matched-aus → matched-ein → kauf/income → swap
// → verkauf eingehalten wird (statt zufaelliger Insertion-Order bei fehlender
// Uhrzeit). Damit zeigt jede Anzeige (PDF, Excel, Detail) dieselbe Reihenfolge,
// in der FIFO die TXs verarbeitet hat.
export function sortTxsAsc(txs) {
  const cmp = buildCausalSort(txs);
  return [...txs].sort(cmp);
}

// Fiat-only Transfers, NFT-Bewegungen und interne Trading-Fee-TXs sind
// Buchungseinheiten ohne User-Mehrwert in der "Alle Transaktionen"-Tabelle.
// Sie blaehen den PDF auf und verwirren. FIFO-Engine verarbeitet sie automatisch.
const PDF_FIAT_SET = new Set(['EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL']);
function shouldHideFromAllTxList(tx) {
  if (tx.typ === 'gebuehr') return true;
  if (tx.typ === 'einzahlung' || tx.typ === 'auszahlung') {
    const sym = String(tx.coin || tx.sym || '').toUpperCase();
    if (PDF_FIAT_SET.has(sym) || sym === 'NFT') return true;
  }
  return false;
}

// ── Wert-Ableitung für Ein-/Auszahlungen (Display-Anreicherung) ────────────
// Transfers (einzahlung/auszahlung) haben oft keinen erfassten EUR-Kurs (preis=0)
// → in der TX-Liste stand „–". Wir KENNEN den Wert aber implizit aus den eigenen
// Trades desselben Coins: ein Verkauf/Tausch desselben Coins liefert einen
// realen Stückpreis (z. B. XLM-Wert aus dem XLM→USDC-Swap, WHALEGURU-Wert aus dem
// SOL→WHALEGURU-Swap). Wir leiten den Transfer-Wert aus dem zeitlich NÄCHSTEN
// eigenen, bepreisten Handel desselben Coins ab. Nur Display/Export — die
// FIFO-Engine bleibt unberührt (rechnet auf den Roh-TXs).
// Swap-Symbole + EUR-Ableitung kommen aus der zentralen Report-Engine
// (reportModel.js) — EINE Whitelist (SWAP_EUR_SET) für PDF, Excel UND dieses
// Oracle, damit ein Swap überall denselben EUR-Wert/Anker ergibt.

// Baut aus allen bepreisten TXs ein Coin→[{ts,unit,anchored}]-Register der real
// gehandelten Stückpreise. Quelle pro Coin: Kauf/Verkauf (preis/menge, in EUR =
// verankert), Swap (effEUR/Menge je Seite). Eine Swap-Seite ist „verankert", wenn
// die ANDERE Seite EUR/Stablecoin ist — dann spiegelt der Stückpreis einen echten
// EUR-Marktwert. Ein volatiler Coin↔Coin-Swap ist NICHT verankert (sein „Preis"
// hängt am Wert des anderen Coins) und darf einen unrelated Transfer nicht
// falsch bewerten (realer Bug: 2.500 XLM zeigten 2.864 € statt ~503 €).
export function buildCoinPriceOracle(txs) {
  // KRITISCH: pro (Coin, Tag, anchored) Menge UND EUR SUMMIEREN, DANN unit=ΣEUR/Σmenge.
  // Importierte Verkäufe/Swaps sind oft in Lot-Stücke zerlegt, deren preis nach
  // KOSTENBASIS verteilt ist (nicht nach Menge) → jedes Einzelstück hat einen
  // Müll-Stückpreis (realer Bug: ein XLM-Stück 199 Stk/228 € = 1,1459 €/XLM, ein
  // XRP-Stück 496 Stk/12,46 € = 0,0251). Die SUMME pro Tag stimmt aber exakt
  // (2.500 XLM = 503,77 € = 0,2015). Aggregation = echter Tages-VWAP, robust.
  const agg = new Map(); // "COIN|YYYY-MM-DD|anchored" → { sym, eur, qty, ts, anchored }
  const addAgg = (sym, eur, qty, ts, anchored) => {
    const s = String(sym || '').toUpperCase();
    const e = Number(eur), q = Number(qty);
    if (!s || !(e > 0) || !(q > 0)) return;
    const t = +new Date(ts);
    if (!Number.isFinite(t)) return;
    const day = String(ts).slice(0, 10);
    const key = `${s}|${day}|${anchored ? 1 : 0}`;
    let bucket = agg.get(key);
    if (!bucket) { bucket = { sym: s, eur: 0, qty: 0, ts, anchored: !!anchored }; agg.set(key, bucket); }
    bucket.eur += e;
    bucket.qty += q;
    if (t < bucket.ts) bucket.ts = t;
  };
  for (const tx of (txs || [])) {
    const ts = tx.effectiveTimestamp || tx.datum;
    if (!ts) continue;
    if (tx.typ === 'swap') {
      const out = swapOutSym(tx);
      const inC = swapInSym(tx);
      const eur = Number(tx.preis) > 0 ? Number(tx.preis) : deriveSwapEurValue(tx);
      const mo = Number(tx.mengeOut) || 0;
      const mi = Number(tx.mengeIn) || 0;
      if (eur > 0 && mo > 0) addAgg(out, eur, mo, ts, SWAP_EUR_SET.has(inC));
      if (eur > 0 && mi > 0) addAgg(inC, eur, mi, ts, SWAP_EUR_SET.has(out));
    } else {
      const p = Number(tx.preis) || 0;
      const m = Number(tx.menge) || 0;
      if (p > 0 && m > 0) addAgg(tx.coin || tx.sym, p, m, ts, true); // direkter EUR-Preis
    }
  }
  // Aggregat → Coin → [{ts, unit, anchored}] (ein Eintrag pro Tag/Anker)
  const map = new Map();
  for (const b of agg.values()) {
    const unit = b.eur / b.qty;
    const t = +new Date(b.ts);
    if (!(unit > 0) || !Number.isFinite(unit) || !Number.isFinite(t)) continue;
    if (!map.has(b.sym)) map.set(b.sym, []);
    map.get(b.sym).push({ ts: t, unit, anchored: b.anchored });
  }
  const pickNearest = (arr, t) => {
    let best = arr[0];
    let bestD = Math.abs(arr[0].ts - t);
    for (const e of arr) {
      const d = Math.abs(e.ts - t);
      if (d < bestD) { best = e; bestD = d; }
    }
    return best.unit;
  };
  return {
    // Stückpreis am zeitlich nächsten Tag (0 = unbekannt). Verankerte Preise
    // (EUR/Stable-basiert) werden bevorzugt; nur wenn es KEINE gibt (z. B. ein
    // Coin, den es nur via Coin↔Coin-Swap gibt — WHALEGURU), zählen alle.
    nearestUnit(sym, dateISO) {
      const arr = map.get(String(sym || '').toUpperCase());
      if (!arr || !arr.length) return 0;
      const anchored = arr.filter(e => e.anchored);
      const pool = anchored.length ? anchored : arr;
      let t = +new Date(dateISO);
      if (!Number.isFinite(t)) t = Math.max(...pool.map(e => e.ts)); // jüngster Preis statt beliebig
      return pickNearest(pool, t);
    },
  };
}

// Füllt fehlende Transfer-Werte aus dem Oracle. Lässt erfasste Werte (preis>0)
// unberührt; markiert abgeleitete Zeilen mit _valueEstimated für den Renderer.
export function enrichTransferValues(displayTxs, oracle) {
  if (!Array.isArray(displayTxs) || !oracle) return displayTxs || [];
  return displayTxs.map((tx) => {
    const isTransfer = tx.typ === 'einzahlung' || tx.typ === 'auszahlung';
    if (!isTransfer || Number(tx.preis) > 0) return tx;
    const sym = tx.coin || tx.sym;
    const menge = Number(tx.menge) || 0;
    if (!sym || !(menge > 0)) return tx;
    const unit = oracle.nearestUnit(sym, tx.datum || dateKey(tx));
    if (!(unit > 0)) return tx;
    return { ...tx, preis: unit * menge, _valueEstimated: true };
  });
}

// Aggregiert alle TXs aus perAsset zu einer eindeutigen, chronologisch sortierten Liste.
// Swap-TXs sind sowohl im coinOut- als auch im coinIn-Asset-Bucket gelistet — das
// führte ohne Dedup zu Duplikaten im "Alle Transaktionen"-Block von PDF + Excel.
export function flattenAllTxs(perAsset) {
  const seen = new Set();
  const out = [];
  for (const bucket of perAsset || []) {
    for (const tx of (bucket.transactions || [])) {
      if (shouldHideFromAllTxList(tx)) continue;
      const key = String(tx._docId || tx.id || '');
      if (!key) { out.push(tx); continue; }
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(tx);
    }
  }
  return mergeTxsByDay(sortTxsAsc(out));
}

// Verdichtet rohe TXs nach (datum, typ, coin/coinOut/coinIn, exchange) — Binance
// splittet einen User-Trade in viele Sekunden-Events, was die "Alle Transaktionen"-
// Tabelle aufblaeht. Nur zusammenfassen, wenn alle Merge-Kriterien identisch sind.
// Summiert menge, mengeOut, mengeIn, preis, gebuehr; bewahrt Notiz/Hash der ersten TX.
//
// WICHTIG: Transfers (einzahlung/auszahlung) werden NIE verdichtet. Jeder Transfer
// ist ein eigenständiges Ereignis (externer Versand vs. Transfer in eigene Wallet,
// gematcht vs. ungematcht, unterschiedliche Ziele) und steuerlich einzeln relevant.
// Sie nach (datum,typ,coin,exchange) zusammenzufassen vermischte z.B. 2 externe
// HBAR-Sends an einen Freund mit eigenen Wallet-Transfers desselben Tags zu einer
// irreführenden Sammelzeile ("1.125 HBAR → Externe" statt 2×375 verschickt). Nur
// Trades/Income (kauf/verkauf/swap/staking…) werden verdichtet — die werden von
// Börsen tatsächlich in Sekunden-Fills gesplittet.
const _NEVER_MERGE_TYPES = new Set(['einzahlung', 'auszahlung']);
export function mergeTxsByDay(txs) {
  if (!Array.isArray(txs) || txs.length === 0) return [];
  const groups = new Map();
  const order = [];
  let _idx = 0;
  for (const tx of txs) {
    const datum = (tx.datum || dateKey(tx) || '').slice(0, 10);
    const key = _NEVER_MERGE_TYPES.has(tx.typ)
      // Eindeutiger Key pro Transfer → niemals zusammengelegt, bleibt eigene Zeile.
      ? `__transfer__|${tx._docId || tx.id || `i${_idx++}`}`
      : [
      datum,
      tx.typ,
      tx.typ === 'swap'
        ? `${tx.coinOut || tx.symOut || ''}>${tx.coinIn || tx.symIn || ''}`
        : (tx.coin || tx.sym || ''),
      tx.exchange || '',
    ].join('|');
    const g = groups.get(key);
    if (!g) {
      const copy = {
        ...tx,
        menge:    Number(tx.menge    || 0),
        mengeOut: Number(tx.mengeOut || 0),
        mengeIn:  Number(tx.mengeIn  || 0),
        preis:    Number(tx.preis    || 0),
        gebuehr:  Number(tx.gebuehr  || 0),
        _count:   1,
      };
      groups.set(key, copy);
      order.push(key);
    } else {
      g.menge    += Number(tx.menge    || 0);
      g.mengeOut += Number(tx.mengeOut || 0);
      g.mengeIn  += Number(tx.mengeIn  || 0);
      g.preis    += Number(tx.preis    || 0);
      g.gebuehr  += Number(tx.gebuehr  || 0);
      g._count   += 1;
    }
  }
  // stueck (Stück-Preis) neu ableiten nach Summierung — sonst zeigen verdichtete
  // Zeilen einen veralteten Per-Stück-Preis aus der ersten TX.
  const out = order.map(k => {
    const g = groups.get(k);
    if (g.typ === 'kauf' || g.typ === 'verkauf') {
      g.stueck = g.menge > 0 ? g.preis / g.menge : 0;
    }
    return g;
  });
  return out;
}

export function getAvailableYears(txs) {
  const set = new Set();
  for (const tx of txs) {
    const y = dateKey(tx).slice(0, 4);
    if (y) set.add(Number(y));
  }
  const arr = [...set].sort((a, b) => b - a);
  const cur = new Date().getFullYear();
  if (!arr.includes(cur)) arr.unshift(cur);
  return arr;
}

function fmtTimestamp() {
  return new Date().toLocaleString('de-DE');
}

// Investiert-Verlauf aus den Transaktionen rekonstruieren
function buildValueSeries(txs, sym) {
  const sorted = sortTxsAsc(txs);
  const series = [];
  let invested = 0;
  for (const tx of sorted) {
    const s = String(sym).toUpperCase();
    if (tx.typ === 'kauf' && String(tx.coin).toUpperCase() === s) {
      invested += Number(tx.preis) || 0;
    } else if (tx.typ === 'verkauf' && String(tx.coin).toUpperCase() === s) {
      // Erlös zieht keine Investition ab; wir zeigen kumulierte Anschaffungskosten
    } else if (tx.typ === 'swap' && String(tx.coinIn).toUpperCase() === s) {
      invested += Number(tx.preis) || 0;
    }
    if (txInvolvesSym(tx, s)) {
      series.push({ date: dateKey(tx), value: invested });
    }
  }
  return series;
}

// ── Coin-Logos: Map aus marketData sammeln ─────────────────────────────────
// Liefert { SYM: 'https://...' } für jeden Coin im Report.
// Wird vom PDF-Builder benutzt, um echte CoinGecko-Logos statt Letter-Badges
// zu rendern. Fallback (Letter-Badge) greift wenn kein Eintrag.
function collectCoinImages(syms, marketData) {
  const out = {};
  syms.forEach((sym) => {
    const cgId = COIN_MAP[sym]?.id;
    const img = marketData?.[cgId]?.image;
    if (img) out[sym] = img;
  });
  return out;
}

function symsInTxs(txs) {
  const set = new Set();
  for (const tx of txs) {
    if (tx.typ === 'swap') {
      if (tx.coinOut) set.add(String(tx.coinOut).toUpperCase());
      if (tx.coinIn) set.add(String(tx.coinIn).toUpperCase());
    } else if (tx.coin) {
      set.add(String(tx.coin).toUpperCase());
    }
  }
  return set;
}

// ── Asset-Export Datenaufbau ────────────────────────────────────────────────

// Echte Daten-Luecke: Erwerbs-TXs (kauf/einzahlung-mit-preis/swap-IN) ohne
// exchange-Feld. Nur DANN ist der "wallet-spezifische FIFO nicht moeglich"-
// Hinweis sinnvoll — Coins zwischen sauber gepflegten Wallets verschieben ist
// kein Daten-Problem.
function hasIncompleteExchangeDataForSym(txs, sym) {
  const target = String(sym).toUpperCase();
  return (txs || []).some(tx => {
    const coinSym = String(tx.coin ?? tx.sym ?? '').toUpperCase();
    const coinInSym = String(tx.coinIn ?? tx.symIn ?? '').toUpperCase();
    const isErwerb =
      (tx.typ === 'kauf' && coinSym === target && Number(tx.preis) > 0) ||
      (tx.typ === 'einzahlung' && coinSym === target && Number(tx.preis) > 0) ||
      (tx.typ === 'swap' && coinInSym === target);
    if (!isErwerb) return false;
    const loc = String(tx.exchange || tx.platform || '').trim();
    return !loc;
  });
}

export function buildAssetExportData({ asset, txs, marketData, isDE, country }) {
  const rules = rulesFor(country || (isDE ? 'DE' : ''));
  // Wert-Anreicherung der Transfers aus dem Oracle (ALLE TXs als Preisquelle, damit
  // z. B. ein WHALEGURU-Transfer den SOL→WHALEGURU-Swap-Wert erbt).
  const priceOracle = buildCoinPriceOracle(txs);
  // Scam-Dust (Address-Poisoning, z.B. 0,000001 XRP) aus PDF/Excel heraushalten —
  // identisch zur On-Screen-Detailansicht (AssetDetailScreen nutzt dropScamDustTxs
  // fuer Haltefrist-Lots UND Activity-Liste). Filter greift in der TX-Liste und in
  // den Haltefrist-Lots (s.u.). Bestand/Stats und die Steuer-Engine (fifo/
  // taxableEvents) bleiben auf den Roh-TXs: Dust ist sub-Cent und erzeugt nie eine
  // Veraeusserung, daher keine Auswirkung auf die Finanzamt-Zahlen.
  const dustFreeTxs = dropScamDustTxs(txs, marketData);
  const assetTxs = enrichTransferValues(
    sortTxsAsc(dustFreeTxs.filter(tx => txInvolvesSym(tx, asset.sym))),
    priceOracle,
  );
  const stats = calcAssetStats(asset.sym, txs);
  const price = marketData?.[asset.coinId]?.price ?? 0;
  const currentValue = stats.menge * price;
  const avgBuy = calcAcquisitionAvgPrice(asset.sym, txs);
  // Haltefrist-Lots (Smart-Check-Daten) nur wo das Feature aktiv ist (DE).
  // Aus dustFreeTxs gebaut — wie AssetDetailScreen (lotTxs) — damit keine
  // Address-Poisoning-Tranchen (0,000001 XRP) in PDF/Excel auftauchen.
  const taxLots = rules.showSmartCheck && stats.menge > 1e-6 ? buildTaxLots(asset.sym, dustFreeTxs) : [];
  const valueSeries = buildValueSeries(txs, asset.sym);
  const image = marketData?.[asset.coinId]?.image ?? null;
  const hasFallback = hasIncompleteExchangeDataForSym(txs, asset.sym);
  // Cash-Flow Sicht: total ever invested, was schon rausgenommen wurde, was noch drin steckt.
  // Investiert = totalInvested (alles je bezahlt, unabhängig von späteren Verkäufen).
  const fifoAll = fifo(txs);
  // Realisierte Veräußerungen dieses Assets (für Veräußerungsübersicht in PDF+Excel
  // der Detailansicht). Quelle je Jurisdiktion: DE=FIFO, AT=gleitender Durchschnitt.
  // Protokoll-Länder ('log') liefern keine Veräußerungsübersicht.
  const symU = String(asset.sym).toUpperCase();
  // AT bewertet nach gleitendem Durchschnitt → Pool EINMAL bauen und sowohl für
  // die Veräußerungs-Events (results) als auch die Restbestands-Kostenbasis
  // (invested, s.u.) nutzen. DE/Default: FIFO, atPool bleibt null.
  const atPool = rules.costBasis === 'average' ? buildAustrianPool(txs) : null;
  const taxEngineResults = atPool ? atPool.results : fifoAll;
  // Anschaffungskosten der Restbestände: DE/Default aus FIFO (stats.invested),
  // AT aus gleitendem Ø (AT-Pool). Menge bleibt jurisdiktionsunabhängig aus
  // buildFifoPool (stats.menge). Siehe arch_jurisdiction_at_tax / taxLots-Divergenz.
  const investedRest = atPool
    ? (austrianRemainingByCoin(atPool.pool)[symU]?.invested ?? 0)
    : stats.invested;
  const taxableEvents = rules.reportMode === 'tax'
    ? mergeTaxableEventsByDay(
        getTaxableEvents(taxEngineResults).filter(ev =>
          String(ev.coin || ev.coinOut || '').toUpperCase() === symU
        )
      )
    : [];
  const inv = calcAssetInvestment(asset.sym, txs, fifoAll);
  const cashIn = calcAssetCashIn(asset.sym, txs);
  const cashOutData = calcAssetCashOut(asset.sym, txs);
  const totalPerformance = (currentValue + cashOutData.cashOut) - cashIn;
  const renditePct = cashIn > 0 ? (totalPerformance / cashIn) * 100 : null;
  const buyCount = txs.filter(tx => {
    const sym = String(asset.sym).toUpperCase();
    const cs = String(tx.coin ?? tx.sym ?? '').toUpperCase();
    const cIn = String(tx.coinIn ?? tx.symIn ?? '').toUpperCase();
    return (tx.typ === 'kauf' && cs === sym && Number(tx.preis) > 0) ||
           (tx.typ === 'swap' && cIn === sym && Number(tx.preis) > 0);
  }).length;

  // Logo-Map: Asset-Sym + alle Swap-Partner
  const allSyms = new Set([String(asset.sym).toUpperCase(), ...symsInTxs(assetTxs)]);
  const coinImages = collectCoinImages(allSyms, marketData);

  return {
    asset: {
      sym: asset.sym,
      name: asset.name || COIN_MAP[asset.sym]?.name || asset.sym,
      coinId: asset.coinId,
      image,
    },
    country: country || '',
    isDE,
    reportMode: rules.reportMode,
    costBasisMethod: rules.costBasis,
    generatedAt: fmtTimestamp(),
    stats: {
      menge: stats.menge,
      invested: investedRest,             // Anschaffungskosten der Restbestände (DE=FIFO, AT=gleitender Ø)
      totalInvested: inv.totalInvested,   // Σ aller Käufe in EUR (User-Sicht "Investiert")
      currentValue,
      avgBuy,
      cashIn,
      cashOut: cashOutData.cashOut,
      cashOutCount: cashOutData.count,
      totalPerformance,
      renditePct,
      buyCount,
      hasFallback,
    },
    transactions: assetTxs,
    taxLots,
    taxableEvents,
    valueSeries,
    coinImages,
  };
}

// ── Jahres-Export Datenaufbau ───────────────────────────────────────────────

export function buildYearExportData({ txs, year, isDE, country, marketData }) {
  const rules = rulesFor(country || (isDE ? 'DE' : ''));
  const yearTxs = txs.filter(tx => txInYear(tx, year));

  const symsInYear = new Set();
  for (const tx of yearTxs) {
    if (tx.typ === 'swap') {
      if (tx.coinOut) symsInYear.add(String(tx.coinOut).toUpperCase());
      if (tx.coinIn) symsInYear.add(String(tx.coinIn).toUpperCase());
    } else if (tx.coin) {
      symsInYear.add(String(tx.coin).toUpperCase());
    }
  }

  // Single Source of Truth: reportEngine liefert alle aggregierten Zahlen.
  // Daneben weiterhin fifo() fuer den vollstaendigen results-Array (z.B. fuer
  // taxableEvents-Liste in der PDF mit allen Feldern wie soldLots).
  const report = buildReport({ txs, year, isDE, country, marketData });
  const fifoAll = fifo(txs);
  // Steuer-Engine je Jurisdiktion (DE=FIFO, AT=gleitender Durchschnitt). AT-Pool
  // EINMAL bauen → speist sowohl die Veräußerungsübersicht (results) als auch die
  // Restbestands-Kostenbasis je Asset (invested, s.u.). Protokoll-Länder: FIFO.
  const atPool = rules.costBasis === 'average' ? buildAustrianPool(txs) : null;
  const taxEngineResults = atPool ? atPool.results : fifoAll;
  // AT: Anschaffungskosten der Restbestände je Coin aus gleitendem Ø statt FIFO
  // (sonst zeigt der AT-Jahresbericht eine FIFO-Nebenzahl). Menge bleibt aus
  // buildFifoPool. DE/Default: FIFO (stats.invested), atRemaining = null.
  const atRemaining = atPool ? austrianRemainingByCoin(atPool.pool) : null;

  const perAsset = [...symsInYear].map(sym => {
    // Filter Gebuehr/Fiat-Transfers raus + pro Tag verdichten (analog zu
    // flattenAllTxs + WalletScreen-Anzeige). Sonst sieht der PDF-Block pro
    // Asset wieder zerrissen aus.
    const txsForSym = mergeTxsByDay(sortTxsAsc(
      yearTxs.filter(tx => txInvolvesSym(tx, sym) && !shouldHideFromAllTxList(tx))
    ));
    const stats = calcAssetStats(sym, txs);
    const coinId = COIN_MAP[sym]?.id;
    const price = marketData?.[coinId]?.price ?? 0;
    const image = marketData?.[coinId]?.image ?? null;
    const currentValue = stats.menge * price;
    const avgBuy = calcAcquisitionAvgPrice(sym, txs);
    const hasFallback = hasIncompleteExchangeDataForSym(txs, sym);
    return {
      asset: {
        sym,
        name: COIN_MAP[sym]?.name || sym,
        coinId,
        image,
      },
      stats: {
        menge: stats.menge,
        // DE/Default=FIFO, AT=gleitender Ø (sym ist hier bereits UPPERCASE).
        invested: atRemaining ? (atRemaining[sym]?.invested ?? 0) : stats.invested,
        currentValue,
        avgBuy,
        hasFallback,
      },
      transactions: txsForSym,
    };
  }).sort((a, b) => b.transactions.length - a.transactions.length);

  // Wert-Anreicherung: Ein-/Auszahlungen ohne erfassten Kurs aus den eigenen
  // bepreisten Trades ableiten (Oracle aus ALLEN TXs, nicht nur dem Jahr — ein
  // Transfer Anfang Januar kann auf einen Trade aus dem Vorjahr zurückgreifen).
  const priceOracle = buildCoinPriceOracle(txs);
  perAsset.forEach((b) => { b.transactions = enrichTransferValues(b.transactions, priceOracle); });

  // Steuer-Jurisdiktionen (DE/AT): verdichtete Veräußerungs-Events aus der
  // jeweiligen Engine. Protokoll-Länder ('log') liefern KEINE Bewertung — der
  // Bericht zeigt dort nur das Anschaffungs-/Veräußerungsprotokoll (allTxs).
  const taxableEvents = rules.reportMode === 'tax'
    ? mergeTaxableEventsByDay(
        getTaxableEvents(taxEngineResults).filter(ev => (ev.datum || '').startsWith(String(year)))
      )
    : [];

  // Logo-Map über alle Coins im Report (perAsset + Swap-Partner in TXs)
  const allSyms = new Set([...symsInYear, ...symsInTxs(yearTxs)]);
  const coinImages = collectCoinImages(allSyms, marketData);

  return {
    year,
    isDE,
    country: country || '',
    reportMode: rules.reportMode,
    costBasisMethod: rules.costBasis,
    generatedAt: fmtTimestamp(),
    perAsset,
    // Kausal sortierte, dedupte Gesamt-TX-Liste fuer PDF/Excel "Alle Transaktionen".
    // Reihenfolge: matched-aus -> matched-ein -> kauf/income -> swap -> verkauf
    // -> unmatched-aus am selben Tag. Damit zeigt der Report die Reihenfolge,
    // in der FIFO die TXs verarbeitet hat (statt zufaelliger Insertion-Order
    // bei fehlender Uhrzeit).
    allTxs: enrichTransferValues(flattenAllTxs(perAsset), priceOracle),
    yearTotals: {
      txCount: yearTxs.length,
      // realized: aus reportEngine — IDENTISCH zur Berichtsseite (Dashboard,
      // Hero-Karte). Funktioniert auch fuer Non-DE (war vorher 0).
      realized: report.realizedPnL,
      // volume: Brutto-Erloes aus Verkaeufen + Swaps (vorher: Summe ALLER preis
      // inkl. Kaeufe — sinnloser Wert, der die PDF-Zahl von der Berichtsseite
      // entkoppelte). Jetzt entspricht "Volumen" der Auszahlung in EUR.
      volume: report.grossProceeds,
      // Neue Felder fuer naechste PDF-Iteration (vorhandene Renderer ignorieren sie):
      grossProceeds: report.grossProceeds,
      totalFees:     report.totalFees,
      costBasis:     report.costBasis,
      taxableGain:   report.taxableGain,
      freeGain:      report.freeGain,
      taxObligation: report.taxObligation,
    },
    taxableEvents,
    // Vollstaendiger Report-Output fuer eine spaetere PDF-Renderer-Iteration,
    // die warnings/perAsset.events/etc. nutzen kann.
    report,
    coinImages,
  };
}
